/**
 * ProfileMenu.tsx
 * 
 * 모바일 프로필 메뉴 컴포넌트 모음
 * - UserInfoCard: 사용자 정보 표시 (이름, 조직, 뱃지)
 * - MainMenuActions: 사용자 액션 메뉴 (설정, 계정전환, 로그아웃 등)
 * - AccountSelectionView: 계정 선택 화면
 * 
 * 주요 사용 위치: Gnb.tsx의 모바일 메뉴 내부
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User as UserIcon, ChevronDown, ChevronLeft, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleBadgeClasses, getRoleLabel, getGuestExpirationDate } from '@/utils/roleUtils';
import { ROUTES } from '@/lib/constants/routes';

// --- Interfaces ---

interface Account {
    accountId: string;
    loginId?: string;
    displayName?: string;
    email?: string;
    profileImageUrl?: string | null;
    organizationId: string;
    organizationName: string;
    role: string;
    license: string;
    isActive?: boolean;
}

interface User {
    id: string | number;
    name: string;
    email: string;
    currentAccount: Account;
    accounts: Account[];
    isAuthenticated: boolean;
    [key: string]: any;
}

interface AuthContextType {
    user: User | null;
    logout: () => void;
    switchAccount: (accountId: string) => boolean;
    getCurrentRole: () => string;
}

// --- Sub-Components ---

interface UserInfoCardProps {
    user: User;
    currentAccount: Account;
    isGuest: boolean;
    getRoleBadgeClasses: (role: string) => string;
    getRoleLabel: (role: string) => string;
}

/**
 * UserInfoCard
 * 모바일 프로필 메뉴 - 사용자 정보 카드
 * Displays the user's name, organization, and badges.
 * 사용 위치: Gnb.tsx의 모바일 메뉴 프로필 섹션 상단
 */
export const UserInfoCard: React.FC<UserInfoCardProps> = ({ user, currentAccount, isGuest, getRoleBadgeClasses, getRoleLabel }) => {
    const { t } = useTranslation('auth');
    const organizationLabel = currentAccount?.organizationName || (
        currentAccount?.role === 'admin'
            ? t('newbaseOrganization', { defaultValue: '뉴베이스' })
            : t('personalUser', { defaultValue: '개인 사용자' })
    );

    return (
        <div className="px-6 py-4">
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{user.name}</h4>
                    <span
                        className={`px-2 rounded text-xs font-bold border whitespace-nowrap ${getRoleBadgeClasses(currentAccount?.role)}`}
                    >
                        {getRoleLabel(currentAccount?.role)}
                    </span>
                </div>

                <div>
                    <span className="text-sm font-medium text-gray-600 block truncate">
                        {organizationLabel}
                    </span>
                </div>
            </div>
        </div>
    );
};

interface MainMenuActionsProps {
    user: User;
    isGuest: boolean;
    handleEmailVerification: () => void;
    getGuestExpirationDate: (expirationDate?: string | null, locale?: string | null) => string | null;
    handleAddAccount: () => void;
    handleAccountSwitch: (accountId: string) => void;
    navigate: (path: string) => void;
    handleLogout: () => void;
    getRoleBadgeClasses: (role: string) => string;
    getRoleLabel: (role: string) => string;
}

/**
 * MainMenuActions
 * 모바일 프로필 메뉴 - 사용자 액션 목록
 * Displays the list of actions (orders, verify, accounts list, add account, password reset, settings, logout).
 * 사용 위치: Gnb.tsx의 모바일 메뉴 프로필 섹션
 */
export const MainMenuActions: React.FC<MainMenuActionsProps> = ({
    user,
    isGuest,
    handleEmailVerification,
    getGuestExpirationDate,
    handleAddAccount,
    handleAccountSwitch,
    navigate,
    handleLogout,
    getRoleBadgeClasses,
    getRoleLabel
}) => {
    const { t, i18n } = useTranslation('auth');
    const [imageError, setImageError] = React.useState<Record<string, boolean>>({});
    
    const otherAccounts = (user.accounts || []).filter(
        (account) => account.accountId !== user.currentAccount?.accountId
    );
    const hasOtherAccounts = otherAccounts.length > 0;
    const guestExpirationLabel = getGuestExpirationDate(user.guestAccountExpiresAt, i18n.language);

    return (
        <div className="py-2">
            {/* ========== 모바일 프로필 메뉴 액션 리스트 시작 ========== */}
            
            {/* 게스트 사용자: 이메일 인증 버튼 */}
            {isGuest && (
                <button
                    onClick={handleEmailVerification}
                    className="w-full flex items-center gap-3 px-6 py-3 text-start hover:bg-gray-50 transition-colors group"
                >
                    <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-red-500 text-sm">{t('settings.verifyMember')}</span>
                        {guestExpirationLabel && (
                            <span className="text-xs text-gray-400">
                                {guestExpirationLabel} {t('guestExpires')}
                            </span>
                        )}
                    </div>
                </button>
            )}

            {/* 계정 더보기 섹션 */}
            {(hasOtherAccounts || true) && (
                <div className="border-t border-gray-100">
                    {/* 섹션 헤더 */}
                    <div className="px-6 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('accountsSection', { defaultValue: '계정 더보기' })}
                    </div>
                    
                    {/* 다른 계정 목록 */}
                    {otherAccounts.map((account) => {
                        const accountLabel = account.displayName || account.loginId || account.email || '알 수 없음';
                        const hasProfileImage = account.profileImageUrl && account.profileImageUrl.trim() !== '' && !imageError[account.accountId];
                        
                        return (
                            <button
                                key={account.accountId}
                                type="button"
                                onClick={() => handleAccountSwitch(account.accountId)}
                                className="w-full flex items-center gap-3 px-6 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                {/* 프로필 이미지 */}
                                {hasProfileImage ? (
                                    <img
                                        src={account.profileImageUrl!}
                                        alt={accountLabel}
                                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                        onError={() => setImageError(prev => ({ ...prev, [account.accountId]: true }))}
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                        <UserIcon size={16} className="text-gray-400" />
                                    </div>
                                )}
                                
                                {/* 계정 정보 */}
                                <div className="flex flex-col items-start flex-1 min-w-0">
                                    <span className="text-sm font-medium truncate w-full text-left">{accountLabel}</span>
                                    {account.organizationName && (
                                        <span className="text-xs text-gray-500 truncate w-full text-left">{account.organizationName}</span>
                                    )}
                                </div>
                                
                                {/* 역할 뱃지 */}
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${getRoleBadgeClasses(account.role)}`}>
                                    {getRoleLabel(account.role)}
                                </span>
                            </button>
                        );
                    })}
                    
                    {/* 다른 계정 추가 버튼 */}
                    <button
                        type="button"
                        onClick={handleAddAccount}
                        className="w-full flex items-center gap-3 px-6 py-3 text-brand-600 hover:bg-blue-50 transition-colors font-medium"
                    >
                        <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                            <span className="text-brand-600 text-lg font-semibold">+</span>
                        </div>
                        <span className="text-sm text-left">{t('addAccountLabel')}</span>
                    </button>
                </div>
            )}

            {/* 비밀번호 변경 버튼 */}
            <button onClick={() => navigate('/settings/change-password')} className="w-full flex items-center px-6 py-3 text-gray-700 hover:bg-gray-50 transition-colors">
                <span className="text-sm font-medium">{t('changePassword')}</span>
            </button>

            {/* 설정 버튼 */}
            <button onClick={() => navigate('/settings')} className="w-full flex items-center px-6 py-3 text-gray-700 hover:bg-gray-50 transition-colors">
                <span className="text-sm font-medium">{t('settings.pageTitle')}</span>
            </button>

            {/* 로그아웃 버튼 */}
            <button onClick={handleLogout} className="w-full flex items-center px-6 py-3 text-gray-700 hover:bg-gray-50 transition-colors">
                <span className="text-sm font-medium">
                    {hasOtherAccounts ? t('logoutFromAll', { defaultValue: '모든 계정에서 로그아웃' }) : t('logout')}
                </span>
            </button>
            
            {/* ========== 모바일 프로필 메뉴 액션 리스트 끝 ========== */}
        </div>
    );
};

interface AccountSelectionViewProps {
    accounts: Account[];
    currentAccount: Account;
    handleAccountSwitch: (accountId: string) => void;
    onBack: () => void;
    getRoleBadgeClasses: (role: string) => string;
    getRoleLabel: (role: string) => string;
    showHeader?: boolean;
    /** 전환할 다른 계정이 없을 때: 안내 + 추가 이동 */
    onAddAccount?: () => void;
}

/**
 * AccountSelectionView
 * 모바일 프로필 메뉴 - 계정 선택 뷰
 * Displays the list of available accounts for switching.
 * 사용 위치: Gnb.tsx의 모바일 메뉴에서 "다른 계정으로 전환" 클릭 시 표시
 */
export const AccountSelectionView: React.FC<AccountSelectionViewProps> = ({
    accounts,
    currentAccount,
    handleAccountSwitch,
    onBack,
    getRoleBadgeClasses,
    getRoleLabel,
    showHeader = true,
    onAddAccount,
}) => {
    const { t } = useTranslation('auth');
    return (
        <>
            {showHeader && (
                <div className="flex items-center gap-2 px-4 py-3">
                    <button
                        type="button"
                        onClick={onBack}
                        className="p-1 -ms-1 text-gray-500 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                    >
                        <ChevronLeft size={20} className="rtl:rotate-180" />
                    </button>
                    <span className="text-sm font-semibold text-gray-900">{t('accountSelect')}</span>
                </div>
            )}

            <div className="max-h-[300px] overflow-y-auto p-2">
                {accounts.length === 0 && (
                    <div className="px-3 py-5 text-center text-sm text-gray-600">
                        <p className="mb-4 leading-relaxed">{t('switchAccountEmpty')}</p>
                        {onAddAccount && (
                            <button
                                type="button"
                                onClick={onAddAccount}
                                className="inline-flex items-center justify-center rounded-lg border border-brand-500 bg-white px-4 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50"
                            >
                                {t('addAccountLabel')}
                            </button>
                        )}
                    </div>
                )}
                {accounts.map((account) => {
                    const isActive = currentAccount?.accountId === account.accountId;
                    const accountLabel = account.organizationName || (
                        account.role === 'admin'
                            ? t('newbaseOrganization', { defaultValue: '뉴베이스' })
                            : t('personalUser', { defaultValue: '개인 사용자' })
                    );
                    return (
                        <button
                            key={account.accountId}
                            onClick={() => !isActive && handleAccountSwitch(account.accountId)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg text-start transition-colors ${isActive
                                ? 'bg-blue-50 cursor-default'
                                : 'hover:bg-gray-50'
                                }`}
                            disabled={isActive}
                        >
                            <div className="flex flex-col gap-1">
                                <div className={`text-sm font-medium ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                                    {accountLabel}
                                </div>
                                <div className="flex flex-wrap gap-1.5">

                                    <div className={`text-xs font-bold px-1.5 py-0.5 rounded border inline-block w-fit ${getRoleBadgeClasses(account.role)}`}>
                                        {getRoleLabel(account.role)}
                                    </div>
                                </div>
                            </div>
                            {isActive && <Check size={16} className="text-blue-600" />}
                        </button>
                    );
                })}
            </div>
        </>
    );
};

// --- Main Component ---

export default function ProfileMenu() {
    const { user, logout, switchAccount, getCurrentRole } = useAuth() as AuthContextType;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [imageError, setImageError] = useState<boolean>(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset image error when user changes
    useEffect(() => {
        setImageError(false);
    }, [user?.profileImageUrl]);

    if (!user) return null;

    const currentAccount = user.currentAccount;
    const isGuest = getCurrentRole() === 'guest';

    // --- Actions ---
    const handleAccountSwitch = (accountId: string) => {
        const isSwitched = switchAccount(accountId);
        setIsOpen(false);
        if (!isSwitched) {
            // 저장된 로그인 세션이 없는 계정 — 전환하지 않고 해당 계정으로 다시 로그인(연결)하도록 안내
            const targetLoginId = user?.accounts?.find((account) => account.accountId === accountId)?.loginId;
            navigate(ROUTES.AUTH.ADD_ACCOUNT, { state: { reloginLoginId: targetLoginId } });
            return;
        }
        navigate('/');
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleAddAccount = () => {
        setIsOpen(false);
        navigate(ROUTES.AUTH.ADD_ACCOUNT);
    };

    const handleEmailVerification = () => {
        setIsOpen(false);
        navigate('/verify-email');
    };

    return (
        <div ref={menuRef} className="relative">
            {/* Profile Trigger Button (GNB) */}
            <button
                className={`flex items-center gap-2 px-1 py-1 rounded-full transition-colors ${isOpen ? 'bg-blue-50' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                {/* 프로필 이미지 또는 기본 아이콘 */}
                {user.profileImageUrl && user.profileImageUrl.trim() !== '' && !imageError ? (
                    <img
                        src={user.profileImageUrl}
                        alt={user.name}
                        className="w-9 h-9 rounded-full object-cover"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center ${isGuest ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'
                            }`}
                    >
                        <UserIcon size={18} />
                    </div>
                )}
                <div className="hidden lg:block text-sm font-medium text-gray-700 max-w-[100px] truncate">
                    {user.name}
                </div>
                <div className="flex items-center gap-2">
                    <ChevronDown
                        size={16}
                        className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                    />
                </div>
            </button>


            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute end-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[1005] animate-in fade-in slide-in-from-top-2 duration-200">
                    <UserInfoCard
                        user={user}
                        currentAccount={currentAccount}
                        isGuest={isGuest}
                        getRoleBadgeClasses={getRoleBadgeClasses}
                        getRoleLabel={getRoleLabel}
                    />
                    <MainMenuActions
                        user={user}
                        isGuest={isGuest}
                        handleEmailVerification={handleEmailVerification}
                        getGuestExpirationDate={getGuestExpirationDate}
                        handleAddAccount={handleAddAccount}
                        handleAccountSwitch={handleAccountSwitch}
                        navigate={navigate}
                        handleLogout={handleLogout}
                        getRoleBadgeClasses={getRoleBadgeClasses}
                        getRoleLabel={getRoleLabel}
                    />
                </div>
            )}
        </div>
    );
}
